import React, { useState } from 'react';
import { UserPlus, Calendar, FileText, ChevronRight, Search } from 'lucide-react';
import CaseDetailsModal from '../Modals/CaseDetailsModal';

const CaseList = ({ cases = [], token, onSelectCase, onAddNewCase }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCase, setSelectedCase] = useState(null);
  const [showCaseModal, setShowCaseModal] = useState(false);

  // Filter cases based on search term
  const filteredCases = cases.filter(caseItem => 
    caseItem.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    caseItem.caseId.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const handleCaseClick = (caseItem) => {
    setSelectedCase(caseItem);
    setShowCaseModal(true);
  };

  const handleCloseModal = () => {
    setShowCaseModal(false);
    setSelectedCase(null);
  };

  const handleNewAssessment = (caseItem) => {
    onSelectCase(caseItem);
  };

  return (
    <div className="h-[calc(100vh-4rem)] bg-gradient-to-br from-teal-50 to-blue-50 overflow-y-auto">
      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Header */}
        <div className="mb-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Your Case Load</h2>
            <p className="text-gray-600">
              {cases.length} case{cases.length !== 1 ? 's' : ''} with assessment history
            </p>
          </div>
          <button
            onClick={onAddNewCase}
            className="bg-teal-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors flex items-center gap-2"
          >
            <UserPlus size={20} />
            Add New Case
          </button>
        </div>

        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search by case name or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:border-teal-500 focus:outline-none"
          />
        </div>
      </div>

      {/* Case List */}
      {filteredCases.length === 0 ? (
        <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
          {cases.length === 0 ? (
            <>
              <Users className="mx-auto text-gray-400 mb-4" size={64} />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Cases Yet</h3>
              <p className="text-gray-600 mb-6">
                Get started by adding your first case and creating an assessment.
              </p>
              <button
                onClick={onAddNewCase}
                className="bg-teal-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors inline-flex items-center gap-2"
              >
                <UserPlus size={20} />
                Add First Case
              </button>
            </>
          ) : (
            <>
              <Search className="mx-auto text-gray-400 mb-4" size={48} />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Results Found</h3>
              <p className="text-gray-600">
                No cases match your search for "{searchTerm}"
              </p>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredCases.map((caseItem) => (
            <button
              key={caseItem.caseId}
              onClick={() => handleCaseClick(caseItem)}
              className="w-full bg-white border-2 border-gray-200 rounded-lg p-6 hover:border-teal-500 hover:shadow-lg transition-all duration-200 text-left group"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-bold text-gray-900 group-hover:text-teal-600 transition-colors">
                      {caseItem.name}
                    </h3>
                    <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                      ID: {caseItem.caseId}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-6 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <FileText size={16} />
                      <span>{caseItem.assessmentCount} assessment{caseItem.assessmentCount !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar size={16} />
                      <span>Last: {formatDate(caseItem.lastAssessmentDate)}</span>
                    </div>
                  </div>
                </div>
                
                <ChevronRight className="text-gray-400 group-hover:text-teal-600 group-hover:translate-x-1 transition-all" size={24} />
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Case Details Modal */}
      <CaseDetailsModal
        isOpen={showCaseModal}
        onClose={handleCloseModal}
        case={selectedCase}
        token={token}
        onNewAssessment={handleNewAssessment}
      />
      </div>
    </div>
  );
};

// For the empty state icon
const Users = ({ size, className }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
);

export default CaseList;

